import React, { useState, useEffect } from 'react';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';
import { useNavigate } from 'react-router-dom';

const SuperSimpleProviderDashboard = () => {
  const { user, sessionToken } = useUnifiedAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('services');
  const [selectedDate, setSelectedDate] = useState('2025-08-02');
  const [availability, setAvailability] = useState({});
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [operatingHours, setOperatingHours] = useState({
    opening_time: '08:00',
    closing_time: '18:00',
    break_start: '12:00',
    break_end: '14:00'
  });

  // Staff Management State
  const [staff, setStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [newStaff, setNewStaff] = useState({
    name: '',
    email: '',
    phone: '',
    specialties: [],
    hourly_rate: '',
    bio: ''
  });
  const [staffSchedules, setStaffSchedules] = useState({});

  // Generate time slots based on operating hours
  const generateTimeSlots = () => {
    const slots = [];
    const start = parseInt(operatingHours.opening_time.split(':')[0]);
    const end = parseInt(operatingHours.closing_time.split(':')[0]);
    const breakStart = parseInt(operatingHours.break_start.split(':')[0]);
    const breakEnd = parseInt(operatingHours.break_end.split(':')[0]);

    for (let hour = start; hour < end; hour++) {
      if (hour >= breakStart && hour < breakEnd) continue; // Skip break time
      
      ['00', '30'].forEach(minute => {
        const time = `${hour.toString().padStart(2, '0')}:${minute}`;
        slots.push(time);
      });
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Next 7 days only
  const generateNext7Days = () => {
    const days = [];
    const today = new Date();
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      days.push({ date: dateStr, display: dayName, isToday: i === 0 });
    }
    return days;
  };

  const next7Days = generateNext7Days();

  // Mock data
  useEffect(() => {
    const mockServices = [
      {
        id: 'service-1',
        name: 'General Consultation',
        duration: 30,
        price: 25,
        description: 'General health consultation',
        active: true
      },
      {
        id: 'service-2',
        name: 'Laser Hair Removal',
        duration: 60,
        price: 50,
        description: 'Full body laser hair removal session',
        active: true
      },
      {
        id: 'service-3',
        name: 'Facial Treatment',
        duration: 90,
        price: 40,
        description: 'Deep cleansing facial treatment',
        active: true
      },
      {
        id: 'service-4',
        name: 'Massage Therapy',
        duration: 45,
        price: 35,
        description: 'Relaxing therapeutic massage',
        active: true
      }
    ];

    // Demo staff members for salon example
    const mockStaff = [
      {
        id: 'staff-1',
        name: 'John Smith',
        email: 'john@salon.com',
        phone: '+965 5123 4567',
        specialties: ['Hair Cutting', 'Hair Styling', 'Hair Color'],
        hourly_rate: 30,
        is_active: true,
        bio: 'Senior hair stylist with 8 years experience'
      },
      {
        id: 'staff-2',
        name: 'Karen Williams',
        email: 'karen@salon.com',
        phone: '+965 5234 5678',
        specialties: ['Facial Treatment', 'Skincare', 'Massage'],
        hourly_rate: 35,
        is_active: true,
        bio: 'Licensed esthetician specializing in anti-aging treatments'
      },
      {
        id: 'staff-3',
        name: 'Moe Johnson',
        email: 'moe@salon.com',
        phone: '+965 5345 6789',
        specialties: ['Laser Hair Removal', 'Body Treatments'],
        hourly_rate: 40,
        is_active: true,
        bio: 'Certified laser technician with advanced training'
      },
      {
        id: 'staff-4',
        name: 'Kris Davis',
        email: 'kris@salon.com',
        phone: '+965 5456 7890',
        specialties: ['Nail Care', 'Manicure', 'Pedicure'],
        hourly_rate: 25,
        is_active: true,
        bio: 'Professional nail technician with creative designs'
      },
      {
        id: 'staff-5',
        name: 'Lucy Brown',
        email: 'lucy@salon.com',
        phone: '+965 5567 8901',
        specialties: ['Massage Therapy', 'Aromatherapy', 'Relaxation'],
        hourly_rate: 28,
        is_active: false,
        bio: 'Therapeutic massage specialist (currently on leave)'
      }
    ];

    const mockAvailability = {
      'service-1': {
        '2025-08-02': ['09:00', '09:30', '10:00', '14:00', '15:00'],
        '2025-08-03': ['08:00', '10:00', '16:00'],
      },
      'service-2': {
        '2025-08-02': ['09:00', '14:00', '15:00'],
        '2025-08-03': ['08:00', '16:00'],
      }
    };
    
    const mockAppointments = [
      {
        id: 1,
        date: '2025-08-02',
        time: '10:00',
        patient: 'Sarah Johnson',
        service: 'General Consultation',
        service_id: 'service-1',
        duration: 30
      },
      {
        id: 2,
        date: '2025-08-03',
        time: '10:00',
        patient: 'Ahmed Al-Rashid', 
        service: 'Laser Hair Removal',
        service_id: 'service-2',
        duration: 60
      }
    ];

    setServices(mockServices);
    setStaff(mockStaff);
    setAvailability(mockAvailability);
    setAppointments(mockAppointments);
  }, []);

  const isTimeAvailable = (time) => {
    if (!selectedService) return false;
    return availability[selectedService]?.[selectedDate]?.includes(time) || false;
  };

  const isTimeBooked = (time) => {
    return appointments.some(apt => apt.date === selectedDate && apt.time === time);
  };

  const getConflictingSlots = (time, duration) => {
    const slots = [];
    const [hour, minute] = time.split(':').map(Number);
    const startMinutes = hour * 60 + minute;
    
    for (let i = 0; i < duration; i += 30) {
      const slotMinutes = startMinutes + i;
      const slotHour = Math.floor(slotMinutes / 60);
      const slotMinute = slotMinutes % 60;
      const slotTime = `${slotHour.toString().padStart(2, '0')}:${slotMinute.toString().padStart(2, '0')}`;
      slots.push(slotTime);
    }
    return slots;
  };

  const toggleTimeSlot = (time) => {
    if (!selectedService) return;
    
    const service = services.find(s => s.id === selectedService);
    if (!service) return;

    const currentSlots = availability[selectedService]?.[selectedDate] || [];
    const conflictSlots = getConflictingSlots(time, service.duration);
    
    if (currentSlots.includes(time)) {
      // Remove time slot and all its duration slots
      const newSlots = currentSlots.filter(slot => !conflictSlots.includes(slot));
      setAvailability({
        ...availability,
        [selectedService]: {
          ...availability[selectedService],
          [selectedDate]: newSlots
        }
      });
    } else {
      // Check if any conflicting slot is booked
      const hasBookedConflict = conflictSlots.some(slot => 
        appointments.some(apt => apt.date === selectedDate && apt.time === slot)
      );
      
      if (hasBookedConflict) {
        alert('Cannot set availability - conflicts with existing booking');
        return;
      }
      
      // Add all required time slots for this service
      const newSlots = [...new Set([...currentSlots, ...conflictSlots])].sort();
      setAvailability({
        ...availability,
        [selectedService]: {
          ...availability[selectedService],
          [selectedDate]: newSlots
        }
      });
    }
  };

  const addService = () => {
    const serviceName = prompt('Enter service name:');
    const serviceDuration = parseInt(prompt('Enter duration in minutes (30, 60, 90):') || '30');
    const servicePrice = parseFloat(prompt('Enter price in KD:') || '25');
    
    if (serviceName) {
      const newService = {
        id: `service-${Date.now()}`,
        name: serviceName,
        duration: serviceDuration,
        price: servicePrice,
        description: '',
        active: true
      };
      setServices([...services, newService]);
    }
  };

  const toggleServiceActive = (serviceId) => {
    setServices(services.map(service => 
      service.id === serviceId 
        ? { ...service, active: !service.active }
        : service
    ));
  };

  const deleteService = async (serviceId, serviceName) => {
    // Check if user is authenticated
    if (!user || !sessionToken) {
      alert('❌ You must be logged in to delete services.');
      return;
    }

    // Show confirmation dialog
    const confirmDelete = window.confirm(
      `Are you sure you want to permanently delete "${serviceName}"?\n\nThis action cannot be undone and will:\n• Remove all availability settings for this service\n• Cancel any future bookings for this service`
    );
    
    if (!confirmDelete) {
      return;
    }

    try {
      // Get the backend URL from environment variables
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Use the user ID as provider ID (assuming provider users have their own profile)
      const providerId = user.id;
      
      // Make API call to delete the service
      const response = await fetch(`${backendUrl}/api/providers/${providerId}/services/${serviceId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`,
          'X-Session-ID': sessionToken // Some endpoints might use this header
        }
      });

      if (response.ok) {
        // Remove service from local state
        setServices(services.filter(service => service.id !== serviceId));
        
        // Show success message
        alert(`✅ Service "${serviceName}" has been permanently deleted.`);
        
        // If this was the selected service for availability, clear selection
        if (selectedService?.id === serviceId) {
          setSelectedService(null);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete service');
      }
    } catch (error) {
      console.error('Error deleting service:', error);
      alert(`❌ Failed to delete service: ${error.message}`);
    }
  };

  const updateOperatingHours = () => {
    const opening = prompt('Opening time (HH:MM):', operatingHours.opening_time);
    const closing = prompt('Closing time (HH:MM):', operatingHours.closing_time);
    const breakStart = prompt('Break start (HH:MM):', operatingHours.break_start);
    const breakEnd = prompt('Break end (HH:MM):', operatingHours.break_end);
    
    setOperatingHours({
      opening_time: opening || operatingHours.opening_time,
      closing_time: closing || operatingHours.closing_time,
      break_start: breakStart || operatingHours.break_start,
      break_end: breakEnd || operatingHours.break_end
    });
  };

  // Staff Management Functions
  const addStaffMember = async () => {
    if (!newStaff.name.trim()) {
      alert('Please enter staff member name');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // First, find the provider ID for this user
      let providerId = user?.provider_id;
      
      if (!providerId) {
        // Try to find provider by user ID in healthcare_providers collection
        const providerResponse = await fetch(`${backendUrl}/api/healthcare-providers`);
        if (providerResponse.ok) {
          const providerData = await providerResponse.json();
          const userProvider = providerData.providers?.find(p => p.user_id === user?.id);
          if (userProvider) {
            providerId = userProvider.id;
          }
        }
      }
      
      if (!providerId) {
        throw new Error('Provider not found. Please ensure you are logged in as a provider.');
      }

      const response = await fetch(`${backendUrl}/api/providers/${providerId}/staff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({
          name: newStaff.name,
          email: newStaff.email || null,
          phone: newStaff.phone || null,
          specialties: Array.isArray(newStaff.specialties) ? newStaff.specialties : [newStaff.specialties],
          hourly_rate: newStaff.hourly_rate ? parseFloat(newStaff.hourly_rate) : null,
          bio: newStaff.bio || ''
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Staff creation error:', errorData);
        throw new Error(errorData.detail || `Failed to add staff member (${response.status})`);
      }

      const staffMember = await response.json();
      setStaff([...staff, staffMember]);
      
      // Reset form
      setNewStaff({
        name: '',
        email: '',
        phone: '',
        specialties: [],
        hourly_rate: '',
        bio: ''
      });
      setShowAddStaffModal(false);
      
      alert(`✅ Staff member "${newStaff.name}" added successfully!`);
    } catch (error) {
      console.error('Error adding staff member:', error);
      alert(`❌ Failed to add staff member: ${error.message}`);
    }
  };

  const deleteStaffMember = async (staffId, staffName) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to remove "${staffName}" from your team?\n\nThis will:\n• Remove their schedule\n• Cancel their future appointments\n• Remove them from the system`
    );
    
    if (!confirmDelete) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const providerId = user?.id || 'demo-provider-id';

      const response = await fetch(`${backendUrl}/api/providers/${providerId}/staff/${staffId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
        },
      });

      if (response.ok) {
        setStaff(staff.filter(member => member.id !== staffId));
        if (selectedStaff?.id === staffId) {
          setSelectedStaff(null);
        }
        alert(`✅ Staff member "${staffName}" removed successfully.`);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to remove staff member');
      }
    } catch (error) {
      console.error('Error removing staff member:', error);
      alert(`❌ Failed to remove staff member: ${error.message}`);
    }
  };

  const toggleStaffActive = (staffId) => {
    setStaff(staff.map(member => 
      member.id === staffId 
        ? { ...member, is_active: !member.is_active }
        : member
    ));
  };

  const saveChanges = () => {
    alert('✅ Availability saved successfully!');
    console.log('Saved availability:', availability);
    console.log('Operating hours:', operatingHours);
    console.log('Services:', services);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Simple Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-lg mx-auto px-4 py-4">
          <h1 className="text-xl font-bold text-gray-900">Provider Dashboard</h1>
          <p className="text-sm text-gray-600">Set your available times</p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Profile Management Quick Access */}
        <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-blue-900">Provider Profile</h3>
              <p className="text-xs text-blue-700">Manage your facility pictures and profile information</p>
            </div>
            <button
              onClick={() => navigate('/profile')}
              className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-colors"
            >
              📸 Manage Photos
            </button>
          </div>
        </div>

        {/* Enhanced Tab Navigation - Now with 4 tabs */}
        <div className="grid grid-cols-2 gap-1 bg-gray-100 rounded-lg p-1 mb-6">
          <button
            onClick={() => setActiveTab('services')}
            className={`py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'services'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600'
            }`}
          >
            🏥 Services
          </button>
          <button
            onClick={() => setActiveTab('staff')}
            className={`py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'staff'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600'
            }`}
          >
            👥 Staff
          </button>
          <button
            onClick={() => setActiveTab('availability')}
            className={`py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'availability'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600'
            }`}
          >
            📅 Set Times
          </button>
          <button
            onClick={() => setActiveTab('appointments')}
            className={`py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'appointments'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600'
            }`}
          >
            📋 Bookings
          </button>
        </div>

        {/* Services Management Tab */}
        {activeTab === 'services' && (
          <div>
            {/* Operating Hours */}
            <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-base font-semibold text-gray-900">Operating Hours</h3>
                <button
                  onClick={updateOperatingHours}
                  className="text-blue-600 hover:text-blue-700 text-sm"
                >
                  Edit
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Open:</span> {operatingHours.opening_time}
                </div>
                <div>
                  <span className="font-medium">Close:</span> {operatingHours.closing_time}
                </div>
                <div>
                  <span className="font-medium">Break:</span> {operatingHours.break_start} - {operatingHours.break_end}
                </div>
              </div>
            </div>

            {/* Services List */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-base font-semibold text-gray-900">My Services ({services.length})</h3>
                <button
                  onClick={addService}
                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                >
                  + Add
                </button>
              </div>
              
              <div className="space-y-3">
                {services.map((service) => (
                  <div key={service.id} className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900">{service.name}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            service.active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {service.active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{service.description || 'No description'}</p>
                        <div className="flex gap-4 text-xs text-gray-500">
                          <span>⏱️ {service.duration} min</span>
                          <span>💰 {service.price} KD</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => toggleServiceActive(service.id)}
                          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                            service.active
                              ? 'bg-red-50 text-red-700 hover:bg-red-100'
                              : 'bg-green-50 text-green-700 hover:bg-green-100'
                          }`}
                        >
                          {service.active ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => deleteService(service.id, service.name)}
                          className="px-3 py-1 rounded text-xs font-medium bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors"
                          title="Permanently delete this service"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Services Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 text-sm mb-2">Service Management:</h4>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>1. Set your operating hours first</li>
                <li>2. Add services with duration and pricing</li>
                <li>3. Enable/disable services as needed</li>
                <li>4. Use 🗑️ Delete to permanently remove services you no longer offer</li>
                <li>5. Set availability for each service separately</li>
              </ul>
            </div>
          </div>
        )}

        {/* Staff Management Tab */}
        {activeTab === 'staff' && (
          <div>
            {/* Staff Header with Add Button */}
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-base font-semibold text-gray-900">Team Members</h3>
              <button
                onClick={() => setShowAddStaffModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                + Add Staff
              </button>
            </div>

            {/* Staff List */}
            <div className="space-y-3 mb-6">
              {staff.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <div className="text-4xl mb-2">👥</div>
                  <p className="text-gray-600 text-sm">No staff members added yet</p>
                  <p className="text-gray-500 text-xs">Click "Add Staff" to add your team members</p>
                </div>
              ) : (
                staff.map((member) => (
                  <div key={member.id} className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900">{member.name}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            member.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {member.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        
                        {member.email && (
                          <p className="text-sm text-gray-600">📧 {member.email}</p>
                        )}
                        {member.phone && (
                          <p className="text-sm text-gray-600">📱 {member.phone}</p>
                        )}
                        
                        {member.specialties && member.specialties.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-gray-500 mb-1">Specialties:</p>
                            <div className="flex flex-wrap gap-1">
                              {member.specialties.map((specialty, index) => (
                                <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                  {specialty}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {member.hourly_rate && (
                          <p className="text-sm text-gray-600 mt-1">💰 {member.hourly_rate} KD/hour</p>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <button
                          onClick={() => toggleStaffActive(member.id)}
                          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                            member.is_active
                              ? 'bg-red-50 text-red-700 hover:bg-red-100'
                              : 'bg-green-50 text-green-700 hover:bg-green-100'
                          }`}
                        >
                          {member.is_active ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => deleteStaffMember(member.id, member.name)}
                          className="px-3 py-1 rounded text-xs font-medium bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors"
                          title="Remove staff member"
                        >
                          🗑️ Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Staff Management Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 text-sm mb-2">👥 Staff Management:</h4>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>1. Add team members (John, Karen, Moe, etc.)</li>
                <li>2. Set their specialties and hourly rates</li>
                <li>3. Each staff member can have individual schedules</li>
                <li>4. Customers can book with specific staff members</li>
                <li>5. Use individual scheduling in Set Times tab</li>
              </ul>
            </div>

            {/* Add Staff Modal */}
            {showAddStaffModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg max-w-md w-full p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Add New Staff Member</h3>
                    <button
                      onClick={() => setShowAddStaffModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                      <input
                        type="text"
                        value={newStaff.name}
                        onChange={(e) => setNewStaff({...newStaff, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., John Smith"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={newStaff.email}
                        onChange={(e) => setNewStaff({...newStaff, email: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="john@example.com"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={newStaff.phone}
                        onChange={(e) => setNewStaff({...newStaff, phone: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="+965 XXXX XXXX"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate (KD)</label>
                      <input
                        type="number"
                        value={newStaff.hourly_rate}
                        onChange={(e) => setNewStaff({...newStaff, hourly_rate: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="25"
                        step="0.5"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                      <textarea
                        value={newStaff.bio}
                        onChange={(e) => setNewStaff({...newStaff, bio: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Brief description of their expertise..."
                        rows="3"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => setShowAddStaffModal(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={addStaffMember}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      Add Staff
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Service-Based Availability Tab */}
        {activeTab === 'availability' && (
          <div>
            {/* Service Selector */}
            <div className="mb-6">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Select Service</h3>
              <div className="grid grid-cols-1 gap-2">
                {services.filter(s => s.active).map((service) => (
                  <button
                    key={service.id}
                    onClick={() => setSelectedService(service.id)}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      selectedService === service.id
                        ? 'border-blue-300 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium text-gray-900">{service.name}</h4>
                        <p className="text-sm text-gray-600">{service.duration} min • {service.price} KD</p>
                      </div>
                      {selectedService === service.id && (
                        <div className="text-blue-600">✓</div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {selectedService && (
              <div>
                {/* Simple Day Selector */}
                <div className="mb-6">
                  <h3 className="text-base font-semibold text-gray-900 mb-3">Choose Day</h3>
                  <div className="grid grid-cols-7 gap-2">
                    {next7Days.map((day) => (
                      <button
                        key={day.date}
                        onClick={() => setSelectedDate(day.date)}
                        className={`p-2 rounded-lg text-xs font-medium transition-colors ${
                          selectedDate === day.date
                            ? 'bg-blue-600 text-white'
                            : day.isToday
                            ? 'bg-green-100 text-green-800'
                            : 'bg-white text-gray-700 border border-gray-200'
                        }`}
                      >
                        {day.display}
                        {day.isToday && <div className="text-xs mt-1">Today</div>}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Enhanced Time Grid */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-base font-semibold text-gray-900">
                      Set Times for {services.find(s => s.id === selectedService)?.name}
                    </h3>
                    <button
                      onClick={saveChanges}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                    >
                      Save
                    </button>
                  </div>

                  {/* Morning Times */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">🌅 Morning</h4>
                    <div className="grid grid-cols-4 gap-2">
                      {timeSlots.filter(time => parseInt(time.split(':')[0]) < 12).map((time) => {
                        const available = isTimeAvailable(time);
                        const booked = isTimeBooked(time);
                        const service = services.find(s => s.id === selectedService);
                        const conflictSlots = getConflictingSlots(time, service?.duration || 30);
                        const hasConflict = conflictSlots.some(slot => 
                          appointments.some(apt => apt.date === selectedDate && apt.time === slot)
                        );
                        
                        return (
                          <button
                            key={time}
                            onClick={() => !booked && !hasConflict && toggleTimeSlot(time)}
                            disabled={booked || hasConflict}
                            className={`p-3 rounded-lg text-sm font-medium transition-colors ${
                              booked || hasConflict
                                ? 'bg-red-100 text-red-700 border border-red-200'
                                : available
                                ? 'bg-green-100 text-green-800 border border-green-200'
                                : 'bg-white text-gray-700 border border-gray-200 hover:bg-blue-50'
                            }`}
                          >
                            {time}
                            {booked && <div className="text-xs mt-1">Booked</div>}
                            {hasConflict && !booked && <div className="text-xs mt-1">Conflict</div>}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Afternoon Times */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">🌇 Afternoon</h4>
                    <div className="grid grid-cols-4 gap-2">
                      {timeSlots.filter(time => parseInt(time.split(':')[0]) >= 12).map((time) => {
                        const available = isTimeAvailable(time);
                        const booked = isTimeBooked(time);
                        const service = services.find(s => s.id === selectedService);
                        const conflictSlots = getConflictingSlots(time, service?.duration || 30);
                        const hasConflict = conflictSlots.some(slot => 
                          appointments.some(apt => apt.date === selectedDate && apt.time === slot)
                        );
                        
                        return (
                          <button
                            key={time}
                            onClick={() => !booked && !hasConflict && toggleTimeSlot(time)}
                            disabled={booked || hasConflict}
                            className={`p-3 rounded-lg text-sm font-medium transition-colors ${
                              booked || hasConflict
                                ? 'bg-red-100 text-red-700 border border-red-200'
                                : available
                                ? 'bg-green-100 text-green-800 border border-green-200'
                                : 'bg-white text-gray-700 border border-gray-200 hover:bg-blue-50'
                            }`}
                          >
                            {time}
                            {booked && <div className="text-xs mt-1">Booked</div>}
                            {hasConflict && !booked && <div className="text-xs mt-1">Conflict</div>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Enhanced Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 text-sm mb-2">Service-Based Scheduling:</h4>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li>1. Select a service above</li>
                    <li>2. Choose a day</li>
                    <li>3. Tap times to make available (considers service duration)</li>
                    <li>4. Green = available, Red = booked/conflict, White = not set</li>
                    <li>5. System prevents overlapping bookings automatically</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'appointments' && (
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-4">
              Upcoming Appointments ({appointments.length})
            </h3>
            
            {appointments.length === 0 ? (
              <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
                <div className="text-4xl mb-2">📅</div>
                <h4 className="font-medium text-gray-900 mb-1">No appointments yet</h4>
                <p className="text-sm text-gray-600">Set your services and availability first!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {appointments.map((appointment) => (
                  <div key={appointment.id} className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">{appointment.patient}</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          📅 {new Date(appointment.date).toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </p>
                        <p className="text-sm text-gray-600">⏰ {appointment.time} ({appointment.duration} min)</p>
                        <p className="text-sm text-gray-600">🔧 {appointment.service}</p>
                      </div>
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                        Confirmed
                      </span>
                    </div>
                    
                    {/* Service-specific actions */}
                    <div className="flex gap-2 mt-3">
                      <button className="flex-1 bg-blue-50 text-blue-700 px-3 py-2 rounded text-xs font-medium hover:bg-blue-100 transition-colors">
                        📋 View Details
                      </button>
                      <button className="flex-1 bg-green-50 text-green-700 px-3 py-2 rounded text-xs font-medium hover:bg-green-100 transition-colors">
                        📱 Contact User
                      </button>
                      <button className="flex-1 bg-purple-50 text-purple-700 px-3 py-2 rounded text-xs font-medium hover:bg-purple-100 transition-colors">
                        📅 Add to Calendar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperSimpleProviderDashboard;